import React from 'react';
import { useSearchParams } from 'react-router-dom';
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { format } from 'date-fns';
import { cancelAppointment, getAppointmentById, getAppointments, createBookingLink } from '@/lib/api/appointments';
import type { Appointment } from '@/types';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import { toDateOrNull } from '@/utils/date-utils';
import {
  shouldHideAppointmentJoinRescheduleCancel,
  canJoinAppointment,
} from '@/utils/appointments';
import { formatUnderscoreDisplayLabel } from '@/utils/display-labels';
import AppointmentListCard from '@/components/appointments/AppointmentListCard';
import CancelAppointmentDialog, {
  type CancelAppointmentScope,
} from '@/components/appointments/CancelAppointmentDialog';
import ScheduleAppointmentDialog from '@/components/appointments/ScheduleAppointmentDialog';
import { ClientAppointmentSessionDetailView } from '@/clients/details/v2/components/ClientAppointmentSessionDetailView';

interface ClientDetailsV2AppointmentsTabProps {
  clientId: string;
  clientDisplayName?: string;
  clientEmail?: string;
}

type AppointmentFilter = 'upcoming' | 'past';

type AppointmentSession = {
  id: string;
  title: string;
  startTime: Date;
  endTime: Date | null;
  status: string;
  appointmentType: string;
  meetingLink: string;
  location: string;
  notes: string;
  durationMinutes: number | null;
  isRecurring: boolean;
  tags: string[];
  createdByUserType: string;
};

function sessionToCancelAppointment(
  session: AppointmentSession,
  clientId: string,
): Appointment {
  return {
    id: session.id,
    clientId,
    type: session.title,
    date: session.startTime,
    duration: session.durationMinutes ?? 60,
    mode: session.appointmentType,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    isRecurring: session.isRecurring,
    notes: session.notes,
    ...( {
      title: session.title,
      startTime: session.startTime,
      endTime: session.endTime,
      appointmentType: session.appointmentType,
    } as Record<string, unknown>),
  } as Appointment;
}

type RawAppointmentSession = {
  startTime?: unknown;
  start_time?: unknown;
  date?: unknown;
  startDate?: unknown;
  start_date?: unknown;
  endTime?: unknown;
  end_time?: unknown;
  endDate?: unknown;
  end_date?: unknown;
  title?: unknown;
  type?: unknown;
  notes?: unknown;
  description?: unknown;
  duration?: unknown;
  durationMinutes?: unknown;
  tags?: unknown;
  id?: unknown;
  _id?: unknown;
  status?: unknown;
  appointmentType?: unknown;
  mode?: unknown;
  meetingLink?: unknown;
  meeting_link?: unknown;
  location?: unknown;
  isRecurring?: unknown;
  is_recurring?: unknown;
  recurrencePattern?: unknown;
  createdByUserType?: unknown;
};

function parseAppointmentSession(raw: RawAppointmentSession, index: number): AppointmentSession | null {
  const start = toDateOrNull(
    raw?.startTime ??
      raw?.start_time ??
      raw?.date ??
      raw?.startDate ??
      raw?.start_date,
  );

  if (!start) {
    return null;
  }

  const end = toDateOrNull(
    raw?.endTime ??
      raw?.end_time ??
      raw?.endDate ??
      raw?.end_date,
  );

  const title =
    String(raw?.title || raw?.type || '').trim() || 'Session';
  const notes = String(raw?.notes || raw?.description || '').trim();

  const explicitDuration = Number(raw?.duration ?? raw?.durationMinutes);
  const calculatedDuration =
    end && start ? Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000)) : null;
  const durationMinutes = Number.isFinite(explicitDuration)
    ? Math.max(0, Math.round(explicitDuration))
    : calculatedDuration;

  const tags = Array.isArray(raw?.tags)
    ? raw.tags
        .map((item: unknown) => String(item || '').trim())
        .filter(Boolean)
        .slice(0, 4)
    : [];

  return {
    id: String(raw?.id ?? raw?._id ?? `session-${index}-${start.getTime()}`),
    title,
    startTime: start,
    endTime: end,
    status: String(raw?.status || 'active').toLowerCase(),
    appointmentType: String(raw?.appointmentType || raw?.mode || '').toLowerCase(),
    meetingLink: String(raw?.meetingLink || raw?.meeting_link || '').trim(),
    location: String(raw?.location || '').trim(),
    notes,
    durationMinutes,
    isRecurring: Boolean(raw?.isRecurring || raw?.is_recurring || raw?.recurrencePattern),
    tags,
    createdByUserType: String(raw?.createdByUserType || '').toLowerCase(),
  };
}

function getModeLabel(appointmentType: string, location: string): string {
  const type = appointmentType.toLowerCase();
  const normalizedLocation = location.toLowerCase();

  if (type === 'offline' || type === 'in_person' || normalizedLocation.includes('person')) {
    return 'In-Person';
  }

  return 'Video Call';
}

function shouldShowJoinButton(session: AppointmentSession, nowTs: number): boolean {
  if (!session.meetingLink) return false;
  return canJoinAppointment(session.status, session.startTime, session.endTime, {
    startAt: session.startTime,
    durationMinutes: session.durationMinutes,
  });
}

function getPastSessionTags(session: AppointmentSession): string[] {
  if (!session.tags.length) return [];
  return session.tags.filter((tag) => tag.trim().toLowerCase() !== 'video call');
}

function getPastSessionNotes(session: AppointmentSession): string {
  if (session.notes) return session.notes;
  return 'Session notes will appear here once the appointment summary is saved.';
}

export const ClientDetailsV2AppointmentsTab: React.FC<ClientDetailsV2AppointmentsTabProps> = ({
  clientId,
  clientDisplayName = 'Sarah Mitchell',
  clientEmail = 'sarah.mitchell@email.com',
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionQueryId = searchParams.get('session');
  const [sessionDetailAppointment, setSessionDetailAppointment] = React.useState<Appointment | null>(null);
  const [sessionDetailLoading, setSessionDetailLoading] = React.useState(false);
  const [listRefreshKey, setListRefreshKey] = React.useState(0);
  const [activeFilter, setActiveFilter] = React.useState<AppointmentFilter>('upcoming');
  const [sessions, setSessions] = React.useState<AppointmentSession[]>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const limit = 10;
  const [isLoading, setIsLoading] = React.useState(true);
  const [nowTs, setNowTs] = React.useState<number>(() => Date.now());
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = React.useState(false);
  const [appointmentToReschedule, setAppointmentToReschedule] = React.useState<string | null>(null);
  const [isGeneratingLink, setIsGeneratingLink] = React.useState(false);
  const [cancelTargetAppointment, setCancelTargetAppointment] = React.useState<Appointment | null>(null);
  const [isCancellingAppointment, setIsCancellingAppointment] = React.useState(false);

  const handleShareBookingLink = React.useCallback(async () => {
    setIsGeneratingLink(true);
    try {
      const response = await createBookingLink({
        clientId,
        expiresInDays: 1,
      });
      const linkId = response.data?.linkId;
      if (!linkId) {
        throw new Error('No link ID returned');
      }
      const bookingUrl = `${window.location.origin}/book/${linkId}`;
      await navigator.clipboard.writeText(bookingUrl);
      showSuccessToast('Booking link copied', 'The booking link has been copied to your clipboard.');
    } catch (error) {
      showErrorToast(error, 'Failed to generate booking link');
    } finally {
      setIsGeneratingLink(false);
    }
  }, [clientId]);

  React.useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNowTs(Date.now());
    }, 30_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const clearSessionQuery = React.useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('session');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  React.useEffect(() => {
    const id = sessionQueryId?.trim();
    if (!id) {
      setSessionDetailAppointment(null);
      setSessionDetailLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setSessionDetailLoading(true);
      try {
        const { appointment } = await getAppointmentById(id);
        if (cancelled) return;
        const aptClientId = String((appointment as { clientId?: unknown }).clientId ?? '').trim();
        if (aptClientId !== String(clientId).trim()) {
          showErrorToast(new Error('Forbidden'), 'This appointment does not belong to this client.');
          clearSessionQuery();
          setSessionDetailAppointment(null);
          return;
        }
        setSessionDetailAppointment(appointment);
      } catch (error) {
        if (!cancelled) {
          showErrorToast(error, 'Failed to load appointment');
          clearSessionQuery();
          setSessionDetailAppointment(null);
        }
      } finally {
        if (!cancelled) {
          setSessionDetailLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [sessionQueryId, clientId, clearSessionQuery]);

  React.useEffect(() => {
    let isMounted = true;

    const fetchAppointmentsForClient = async () => {
      try {
        setIsLoading(true);

        const response = await getAppointments({
          clientId,
          limit,
          page,
          filter: activeFilter,
        });

        const normalized = (response?.appointments || [])
          .map((session, index) => parseAppointmentSession(session, index))
          .filter((session): session is AppointmentSession => Boolean(session));

        if (isMounted) {
          setSessions(normalized);
          setTotalCount(response?.count || 0);
        }
      } catch (error) {
        console.error('Failed to load client appointments:', error);
        if (isMounted) {
          setSessions([]);
          setTotalCount(0);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchAppointmentsForClient();

    return () => {
      isMounted = false;
    };
  }, [clientId, activeFilter, page, listRefreshKey]);

  const filteredSessions = sessions;

  const openSessionDetail = React.useCallback(
    (appointmentSessionId: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('session', appointmentSessionId);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleCancelSession = React.useCallback(
    (event: React.MouseEvent, session: AppointmentSession) => {
      event.stopPropagation();
      setCancelTargetAppointment(sessionToCancelAppointment(session, clientId));
    },
    [clientId],
  );

  const handleConfirmCancelAppointment = React.useCallback(
    async (scope: CancelAppointmentScope) => {
      if (!cancelTargetAppointment) return;
      setIsCancellingAppointment(true);
      try {
        await cancelAppointment(cancelTargetAppointment.id, scope);
        showSuccessToast('Appointment cancelled', 'This appointment has been cancelled.');
        setCancelTargetAppointment(null);
        setSessionDetailAppointment(null);
        clearSessionQuery();
        setListRefreshKey((key) => key + 1);
      } catch (error) {
        showErrorToast(error, 'Failed to cancel appointment');
      } finally {
        setIsCancellingAppointment(false);
      }
    },
    [cancelTargetAppointment, clearSessionQuery],
  );

  const sessionDetailIsPast = React.useMemo(() => {
    if (!sessionDetailAppointment) return false;
    const raw = sessionDetailAppointment as unknown as Record<string, unknown>;
    const startSrc = raw.startTime ?? raw.start_time ?? sessionDetailAppointment.date;
    const start = startSrc instanceof Date ? startSrc : new Date(String(startSrc));
    const endSrc = raw.endTime ?? raw.end_time;
    const end = endSrc
      ? endSrc instanceof Date
        ? endSrc
        : new Date(String(endSrc))
      : new Date(start.getTime() + Math.max(1, Number(sessionDetailAppointment.duration) || 60) * 60_000);
    const dur =
      Number(sessionDetailAppointment.duration) ||
      Math.max(1, Math.round((end.getTime() - start.getTime()) / 60_000));
    const status = String(raw.status ?? sessionDetailAppointment.status ?? '');
    return (
      shouldHideAppointmentJoinRescheduleCancel(status, end, { startAt: start, durationMinutes: dur }) ||
      activeFilter === 'past'
    );
  }, [sessionDetailAppointment, activeFilter]);

  const handleCloseScheduleDialog = () => {
    setIsScheduleDialogOpen(false);
    setAppointmentToReschedule(null);
  };

  const handleOpenScheduleDialog = () => {
    setAppointmentToReschedule(null);
    setIsScheduleDialogOpen(true);
  };

  const handleOpenRescheduleDialog = React.useCallback((appointmentId: string) => {
    setAppointmentToReschedule(appointmentId);
    setIsScheduleDialogOpen(true);
  }, []);

  const handleRescheduleFromList = React.useCallback(
    (event: React.MouseEvent, sessionId: string) => {
      event.stopPropagation();
      handleOpenRescheduleDialog(sessionId);
    },
    [handleOpenRescheduleDialog],
  );

  React.useEffect(() => {
    const handleOpenRescheduleModal = (event: CustomEvent<{ id?: string }>) => {
      const sessionId = event.detail.id;
      if (!sessionId) return;
      handleOpenRescheduleDialog(sessionId);
    };

    window.addEventListener('open-reschedule-modal', handleOpenRescheduleModal as EventListener);
    return () => {
      window.removeEventListener('open-reschedule-modal', handleOpenRescheduleModal as EventListener);
    };
  }, [handleOpenRescheduleDialog]);

  const handleFilterChange = (newFilter: AppointmentFilter) => {
    setActiveFilter(newFilter);
    setPage(1);
  };

  return (
    <>
      {sessionQueryId ? (
        <Box sx={{ mt: 2.25, display: 'grid', gap: 1.25 }}>
          {sessionDetailLoading ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: '14px',
                border: '1px solid #E4E7EC',
                bgcolor: '#fff',
                py: 6,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={32} />
            </Paper>
          ) : sessionDetailAppointment ? (
            <ClientAppointmentSessionDetailView
              appointment={sessionDetailAppointment}
              isPastSession={sessionDetailIsPast}
              onBack={clearSessionQuery}
              onScheduleSession={handleOpenScheduleDialog}
              onViewAllSessions={clearSessionQuery}
              onReschedule={() => {
                if (sessionDetailAppointment) {
                  handleOpenRescheduleDialog(String(sessionDetailAppointment.id));
                }
              }}
              onCancel={() => {
                if (sessionDetailAppointment) {
                  setCancelTargetAppointment(sessionDetailAppointment);
                }
              }}
              onNotesUpdated={async () => {
                const id = sessionQueryId?.trim();
                if (!id) return;
                try {
                  const { appointment } = await getAppointmentById(id);
                  setSessionDetailAppointment(appointment);
                } catch {
                  /* ignore refresh errors */
                }
              }}
            />
          ) : null}
        </Box>
      ) : null}

      {!sessionQueryId ? (
      <Box sx={{ mt: 2.25, display: 'grid', gap: 1.25 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.25,
        }}
      >
        <Box
          sx={{
            p: 0.5,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            borderRadius: '10px',
            bgcolor: '#F2F4F7',
          }}
        >
          <Button
            disableElevation
            onClick={() => handleFilterChange('upcoming')}
            sx={{
              minHeight: 34,
              px: 1.4,
              borderRadius: '8px',
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.84rem',
              fontWeight: 600,
              color: activeFilter === 'upcoming' ? '#344054' : '#667085',
              bgcolor: activeFilter === 'upcoming' ? '#fff' : 'transparent',
              border: activeFilter === 'upcoming' ? '1px solid #EAECF0' : '1px solid transparent',
              '&:hover': {
                bgcolor: activeFilter === 'upcoming' ? '#fff' : '#F9FAFB',
              },
            }}
          >
            Upcoming
          </Button>
          <Button
            disableElevation
            onClick={() => handleFilterChange('past')}
            sx={{
              minHeight: 34,
              px: 1.4,
              borderRadius: '8px',
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.84rem',
              fontWeight: 600,
              color: activeFilter === 'past' ? '#344054' : '#667085',
              bgcolor: activeFilter === 'past' ? '#fff' : 'transparent',
              border: activeFilter === 'past' ? '1px solid #EAECF0' : '1px solid transparent',
              '&:hover': {
                bgcolor: activeFilter === 'past' ? '#fff' : '#F9FAFB',
              },
            }}
          >
            Past Sessions
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            disableElevation
            onClick={handleShareBookingLink}
            disabled={isGeneratingLink}
            sx={{
              minHeight: 34,
              borderRadius: '8px',
              px: 1.6,
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.78rem',
              fontWeight: 600,
              bgcolor: '#fff',
              color: '#344054',
              border: '1px solid #D0D5DD',
              '&:hover': {
                bgcolor: '#F9FAFB',
              },
              '&.Mui-disabled': {
                opacity: 0.7,
              }
            }}
          >
            {isGeneratingLink ? <CircularProgress size={16} sx={{ mr: 1, color: '#344054' }} /> : null}
            Share Booking Link
          </Button>
          <Button
            disableElevation
            onClick={handleOpenScheduleDialog}
            data-analytics-event="Create Appointment Clicked"
            data-analytics-id="client_details_schedule_session"
            data-analytics-label="Schedule Session"
            data-analytics-section="client_details_appointments_tab"
            sx={{
              minHeight: 34,
              borderRadius: '8px',
              px: 1.6,
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.78rem',
              fontWeight: 600,
              bgcolor: '#3BAEDB',
              color: '#fff',
              '&:hover': {
                bgcolor: '#2D9DCB',
              },
            }}
          >
            Schedule Session +
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ py: 7, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      ) : filteredSessions.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            border: '1px dashed #D0D5DD',
            borderRadius: '12px',
            py: 4.5,
            px: 2,
            textAlign: 'center',
            bgcolor: '#fff',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '0.92rem',
              color: '#344054',
            }}
          >
            {activeFilter === 'upcoming' ? 'No upcoming sessions' : 'No past sessions'}
          </Typography>
          <Typography
            sx={{
              mt: 0.6,
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.8rem',
              color: '#667085',
            }}
          >
            Sessions for this client will appear here.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gap: 1.1 }}>
          {filteredSessions.map((session) => {
            const pastTags = getPastSessionTags(session);

            return activeFilter === 'past' ? (
              <Box
                key={session.id}
                role="button"
                tabIndex={0}
                onClick={() => openSessionDetail(session.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openSessionDetail(session.id);
                  }
                }}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '74px minmax(0, 1fr)',
                  gap: 1.25,
                  alignItems: 'start',
                  cursor: 'pointer',
                  borderRadius: '12px',
                  outline: 'none',
                  '&:focus-visible': {
                    boxShadow: '0 0 0 2px #3BAEDB',
                  },
                }}
              >
                <Box sx={{ textAlign: 'center', pt: 1.2 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '2.1rem',
                      lineHeight: 0.95,
                      color: '#344054',
                    }}
                  >
                    {format(session.startTime, 'dd')}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.35,
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      lineHeight: 1,
                      letterSpacing: '0.06em',
                      color: '#98A2B3',
                    }}
                  >
                    {format(session.startTime, 'MMM').toUpperCase()}
                  </Typography>
                </Box>

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: '12px',
                    border: '1px solid #E4E7EC',
                    bgcolor: '#fff',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      px: 1.6,
                      py: 1.1,
                      borderBottom: '1px solid #EAECF0',
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1.25,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.65 }}>
                        <Typography
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 700,
                            fontSize: '1.02rem',
                            lineHeight: 1.2,
                            color: '#253047',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {session.title}
                        </Typography>
                        {session.isRecurring && (
                          <Typography
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              color: '#12B76A',
                            }}
                          >
                            • Rec
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        sx={{
                          mt: 0.38,
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          color: '#99A1AF',
                        }}
                      >
                        {format(session.startTime, 'h:mm a')} · {getModeLabel(session.appointmentType, session.location)}
                        {session.location ? ` · ${session.location}` : ''}
                        {session.durationMinutes != null ? ` · ${session.durationMinutes} min` : ''}
                        {session.createdByUserType ? ` · Created by ${session.createdByUserType === 'client' ? 'Client' : 'Nutritionist'}` : ''}
                      </Typography>
                    </Box>

                    <Button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openSessionDetail(session.id);
                      }}
                      sx={{
                        minHeight: 26,
                        minWidth: 0,
                        px: 0,
                        py: 0,
                        textTransform: 'none',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        color: '#29A7D8',
                        '&:hover': {
                          bgcolor: 'transparent',
                          color: '#1B94C5',
                        },
                        '& .MuiButton-endIcon': {
                          ml: 0.3,
                        },
                      }}
                    >
                      View Details
                    </Button>
                  </Box>

                  <Box sx={{ px: 1.6, py: 1.05, bgcolor: '#FCFCFD' }}>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: '#99A1AF',
                      }}
                    >
                      Session Notes
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.7,
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.86rem',
                        lineHeight: 1.35,
                        color: '#475467',
                      }}
                    >
                      {getPastSessionNotes(session)}
                    </Typography>

                    {pastTags.length > 0 && (
                      <Box sx={{ mt: 0.9, display: 'flex', flexWrap: 'wrap', gap: 0.65 }}>
                        {pastTags.map((tag, idx) => (
                          <Box
                            key={`${session.id}-tag-${idx}`}
                            sx={{
                              borderRadius: '999px',
                              px: 1.1,
                              py: 0.3,
                              bgcolor: '#EEF4F6',
                              border: '1px solid #E0ECF0',
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                color: '#4B6B7D',
                                lineHeight: 1.2,
                              }}
                            >
                              {formatUnderscoreDisplayLabel(tag)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Box>
            ) : (
              <AppointmentListCard
                key={session.id}
                startTime={session.startTime}
                title={session.title}
                titleTextColor="#000000"
                titleFontWeight={600}
                modeLabel={getModeLabel(session.appointmentType, session.location)}
                createdByLabel={session.createdByUserType ? (session.createdByUserType === 'client' ? 'Client' : 'Nutritionist') : undefined}
                status={session.status}
                dateTextColor="#20AEDC"
                timeTextColor="#20AEDC"
                timeFontWeight={600}
                timeMarginTop={0.85}
                hideActions={shouldHideAppointmentJoinRescheduleCancel(session.status, session.endTime, {
                  startAt: session.startTime,
                  durationMinutes: session.durationMinutes,
                })}
                showJoin={shouldShowJoinButton(session, nowTs)}
                onOpen={() => openSessionDetail(session.id)}
                onCopyRescheduleLink={async (e) => {
                  e.stopPropagation();
                  try {
                    const { createRescheduleLink } = await import('@/lib/api/appointments');
                    const res = await createRescheduleLink({ appointmentId: session.id, expiresInDays: 1 });
                    const link = `${window.location.origin}/reschedule/${res.data.linkId}`;
                    await navigator.clipboard.writeText(link);
                    showSuccessToast('Link Copied', 'Reschedule link copied to clipboard.');
                  } catch (err) {
                    showErrorToast(err, 'Failed to create reschedule link');
                  }
                }}
                onReschedule={(e) => void handleRescheduleFromList(e, session.id)}
                onJoin={(e) => {
                  e.stopPropagation();
                  if (session.meetingLink) {
                    window.open(session.meetingLink, '_blank', 'noopener,noreferrer');
                  }
                }}
                onCancel={(e) => handleCancelSession(e, session)}
              />
            );
          })}
        </Box>
      )}

      {totalCount > limit && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              sx={{
                minWidth: 32,
                height: 32,
                p: 0,
                borderRadius: '8px',
                border: '1px solid #E4E7EC',
                color: '#344054',
                '&:disabled': { opacity: 0.5 },
              }}
            >
              <NavigateBeforeRoundedIcon sx={{ fontSize: 20 }} />
            </Button>
            <Typography sx={{ fontSize: '0.85rem', fontFamily: 'Inter, sans-serif', color: '#667085', px: 1 }}>
              Page {page} of {Math.max(1, Math.ceil(totalCount / limit))}
            </Typography>
            <Button
              disabled={page >= Math.ceil(totalCount / limit)}
              onClick={() => setPage((p) => p + 1)}
              sx={{
                minWidth: 32,
                height: 32,
                p: 0,
                borderRadius: '8px',
                border: '1px solid #E4E7EC',
                color: '#344054',
                '&:disabled': { opacity: 0.5 },
              }}
            >
              <NavigateNextRoundedIcon sx={{ fontSize: 20 }} />
            </Button>
          </Box>
        </Box>
      )}
      </Box>
      ) : null}


      <ScheduleAppointmentDialog
        open={isScheduleDialogOpen}
        onClose={handleCloseScheduleDialog}
        appointmentIdToEdit={appointmentToReschedule}
        defaultClientId={clientId}
        lockedClient={{
          id: clientId,
          name: clientDisplayName,
          email: clientEmail,
        }}
        onAppointmentCreated={() => {
          setListRefreshKey((key) => key + 1);
        }}
        onAppointmentUpdated={async () => {
          setListRefreshKey((key) => key + 1);
          const id = sessionQueryId?.trim();
          if (!id) return;
          try {
            const { appointment } = await getAppointmentById(id);
            setSessionDetailAppointment(appointment);
          } catch {
            /* list refresh is enough */
          }
        }}
      />

      <CancelAppointmentDialog
        open={Boolean(cancelTargetAppointment)}
        onOpenChange={(open) => {
          if (!open) {
            setCancelTargetAppointment(null);
          }
        }}
        appointment={cancelTargetAppointment}
        clientName={clientDisplayName}
        isRecurring={Boolean(cancelTargetAppointment?.isRecurring)}
        isCancelling={isCancellingAppointment}
        onConfirm={handleConfirmCancelAppointment}
      />
    </>
  );
};
